const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const firstText = (...values) => {
    for (const value of values) {
        const cleaned = cleanText(value);
        if (cleaned) return cleaned;
    }
    return '';
};

const normalizeList = (items = []) => {
    const source = Array.isArray(items) ? items : [];
    const values = source
        .map((item) => {
            if (typeof item === 'string') return cleanText(item);
            if (!item || typeof item !== 'object') return '';
            return firstText(
                item.label,
                item.title,
                item.name,
                item.description,
                item.summary,
                item.recommendedCheck,
                item.check,
                item.cause,
                item.issue,
            );
        })
        .filter(Boolean);
    return [...new Set(values)];
};

export function getPartnerRequestVehicleName(request = {}) {
    const vehicle = request?.vehicle || {};
    const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean);
    return parts.length ? parts.join(' ') : firstText(request?.shop_ready_summary?.vehicleName);
}

export function getPartnerRequestIntroVehicleName(request = {}) {
    return firstText(getPartnerRequestVehicleName(request), 'vehicle');
}

const TITLE_INSTRUCTION_PATTERNS = [
    /\s*(?:[.!?]\s*)?Review the ranked likely causes\b.*$/i,
    /\s*(?:[.!?]\s*)?Use the ranked checks below\b.*$/i,
    /\s*(?:[.!?]\s*)?Confirm codes and visual inspection findings\b.*$/i,
    /\s*(?:[.!?]\s*)?Verify the failed part before replacement\b.*$/i,
];

export function cleanPartnerRequestTitle(value = '') {
    let title = cleanText(value);
    TITLE_INSTRUCTION_PATTERNS.forEach((pattern) => {
        title = title.replace(pattern, '');
    });
    title = title
        .replace(/\s+([:;,.!?])/g, '$1')
        .replace(/[.;:\s]+$/g, '')
        .trim();
    return title;
}

export function getPartnerRequestTitle(request = {}, fallback = 'Garage repair request') {
    return firstText(
        cleanPartnerRequestTitle(request?.services?.[0]?.title),
        cleanPartnerRequestTitle(request?.shop_ready_summary?.customerComplaint),
        cleanPartnerRequestTitle(request?.report_snapshot?.customer_complaint),
        cleanPartnerRequestTitle(request?.report_snapshot?.issue),
        fallback,
    );
}

const GUIDED_OUTCOME_LABELS = {
    normal: 'Result was normal / as expected',
    issue: 'Found an issue / result was abnormal',
    not_sure: "Not sure / couldn't complete",
};

function normalizeCompletedChecks(...sources) {
    return sources
        .flatMap((source) => (Array.isArray(source) ? source : []))
        .map((check) => {
            if (!check || typeof check !== 'object') {
                const label = cleanText(check);
                return label ? { recommendedCheck: label, outcomeLabel: '', summary: '', notes: '' } : null;
            }
            const outcome = cleanText(check.outcome).toLowerCase();
            return {
                recommendedCheck: firstText(check.recommendedCheck, check.checkTitle, check.causeTitle, check.title, 'Diagnostic check'),
                outcomeLabel: firstText(check.outcomeLabel, GUIDED_OUTCOME_LABELS[outcome]),
                summary: firstText(check.summary, check.description, check.causeTitle),
                notes: firstText(check.notes, check.note),
            };
        })
        .filter(Boolean)
        .filter((check) => check.recommendedCheck);
}

function normalizeEvidenceMedia(items = []) {
    const seen = new Set();
    return (Array.isArray(items) ? items : [])
        .map((item, index) => {
            if (!item || typeof item !== 'object') return null;
            const url = firstText(item.url, item.preview_url, item.browser_url, item.storage_url);
            const filename = firstText(item.filename, item.original_filename, item.name, `Evidence ${index + 1}`);
            const mimeType = cleanText(item.mime_type || item.file_type);
            const mediaKind = cleanText(item.media_kind || item.attachment_type || item.type).toLowerCase();
            const label = firstText(item.caption, filename, `Evidence ${index + 1}`);
            return {
                id: firstText(item.attachment_id, item.media_id, item.doc_id, `${filename}-${index}`),
                url,
                label,
                filename,
                mimeType,
                isImage: mediaKind === 'photo' || /^image\//i.test(mimeType),
                isVideo: mediaKind === 'video' || /^video\//i.test(mimeType),
            };
        })
        .filter((item) => {
            if (!item || !item.url) return false;
            const key = firstText(item.id, item.url);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
}

export function getPartnerRequestShopReadyReportData(request = {}) {
    const summary = request?.shop_ready_summary || {};
    const service = request?.services?.[0] || {};
    const report = request?.report_snapshot || {};
    const evidenceSource = [
        request?.intake_media,
        summary.evidenceMedia,
        summary.evidence,
        summary.attachments,
        report.evidenceMedia,
        report.evidence,
        report.attachments,
    ].flatMap((items) => (Array.isArray(items) ? items : []));
    const customerComplaint = firstText(
        cleanPartnerRequestTitle(summary.customerComplaint),
        service.summary,
        cleanPartnerRequestTitle(service.title),
        report.customerComplaint,
        report.customer_complaint,
        cleanPartnerRequestTitle(report.mechanicSummary),
        report.issue,
        'Vehicle diagnostic request',
    );
    const shopReadySummary = firstText(
        summary.shopReadySummary,
        summary.mechanicSummary,
        report.mechanicSummary,
        report.mechanic_summary,
        report.summary,
    );
    return {
        customerComplaint,
        shopReadySummary,
        observedSymptoms: normalizeList(
            summary.observedSymptoms || summary.observed_symptoms || report.observedSymptoms || report.symptomsDetailed || report.symptoms,
        ),
        likelyCauses: normalizeList(
            summary.likelyCauses || summary.likely_causes || service.likely_causes || service.likelyCauses || report.likelyCauses || report.likely_causes,
        ),
        completedDiagnosticChecks: normalizeCompletedChecks(
            summary.completedDiagnosticChecks,
            summary.completed_diagnostic_checks,
            report.completedGuidedChecks,
            report.completed_guided_checks,
        ),
        recommendedChecks: normalizeList(
            summary.requestedTests || summary.recommendedChecks || service.requested_tests || service.requestedTests || report.recommendedChecks || report.recommended_checks,
        ),
        evidenceMedia: normalizeEvidenceMedia(evidenceSource),
        vehicleName: getPartnerRequestVehicleName(request),
    };
}

export function buildPartnerRequestShopReadyReportText(request = {}) {
    const report = getPartnerRequestShopReadyReportData(request);
    const lines = [
        'Shop-Ready Report',
        '',
        `Customer Complaint: ${report.customerComplaint}`,
    ];
    if (report.shopReadySummary && report.shopReadySummary !== report.customerComplaint) {
        lines.push('', 'Shop-Ready Summary:', report.shopReadySummary);
    }
    const addList = (label, items) => {
        if (!items.length) return;
        lines.push('', `${label}:`, ...items.map((item) => `- ${item}`));
    };
    addList('Observed Symptoms', report.observedSymptoms);
    addList('Likely Causes', report.likelyCauses);
    if (report.completedDiagnosticChecks.length) {
        lines.push('', 'Completed Diagnostic Checks:');
        report.completedDiagnosticChecks.forEach((check) => {
            const outcome = check.outcomeLabel ? `: ${check.outcomeLabel}` : '';
            const summaryText = check.summary ? `. ${check.summary}` : '';
            const notes = check.notes ? ` Notes: ${check.notes}` : '';
            lines.push(`- ${check.recommendedCheck}${outcome}${summaryText}${notes}`);
        });
    }
    addList('Evidence', report.evidenceMedia.map((item) => item.label));
    addList('Recommended Checks', report.recommendedChecks);
    return lines.join('\n');
}

export function getPartnerRequestInitialMessageText(request = {}) {
    const shopName = firstText(request?.partner_shop?.name, 'there');
    const vehicleName = getPartnerRequestIntroVehicleName(request);
    const vehiclePhrase = vehicleName === 'vehicle' ? 'my vehicle' : `my ${vehicleName}`;
    return `Hey ${shopName}, I'd like to request an estimate and appointment for service to ${vehiclePhrase}.`;
}

export function buildPartnerRequestInitialMessage(request = {}, options = {}) {
    const messageText = getPartnerRequestInitialMessageText(request);
    if (!messageText) return null;
    const driverLabel = firstText(options.driverLabel, 'You');
    return {
        message_id: `initial-${request?.request_id || request?.id || 'request'}`,
        request_id: request?.request_id || request?.id || '',
        sender_role: 'driver',
        sender_label: driverLabel,
        message_text: messageText,
        created_at: request?.created_at || request?.timeline?.find((item) => item?.event === 'submitted')?.created_at || '',
        is_initial_request: true,
        delivery_status: request?.initial_request_delivery_status || null,
        attachments: [{
            type: 'shop_ready_report',
            request_id: request?.request_id || request?.id || '',
        }],
    };
}

// Status/estimate milestone events (from request.timeline) that are NOT chat messages and NOT the
// initial 'submitted' event. These get folded into the conversation so estimates, notifications,
// and status updates appear inline and IN ORDER with the chat — not in a separate section.
const CONVERSATION_TIMELINE_EVENTS = new Set([
    'estimate_sent', 'estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup',
    'picked_up', 'completed', 'declined', 'cancelled', 'failed', 'handoff', 'reschedule_requested',
]);

function buildTimelineConversationItems(request = {}) {
    const timeline = Array.isArray(request?.timeline) ? request.timeline : [];
    const estimates = Array.isArray(request?.estimates) ? request.estimates : [];
    const latestEstimate = request?.latest_estimate || null;
    // Match each estimate_sent event to its OWN estimate version (by id), so a revision adds a new
    // card in the stream instead of overwriting the previous one. Fall back to the latest estimate
    // for older payloads that don't carry per-event ids or the estimates list.
    const estimateById = new Map(estimates.map((e) => [String(e.estimate_id), e]));
    const requestId = request?.request_id || request?.id || '';
    return timeline
        .filter((ev) => ev && CONVERSATION_TIMELINE_EVENTS.has(ev.event))
        .map((ev, i) => {
            const estimate = ev.event === 'estimate_sent'
                ? (estimateById.get(String(ev.estimate_id)) || latestEstimate)
                : null;
            const appointment = ev.event === 'scheduled'
                ? {
                    request_id: requestId,
                    ticket_number: request?.ticket_number || '',
                    scheduled_start_at: request?.scheduled_start_at || '',
                    scheduled_end_at: request?.scheduled_end_at || '',
                    scheduled_timezone: request?.scheduled_timezone || '',
                    schedule_notes: request?.schedule_notes || '',
                    partner_shop: request?.partner_shop || {},
                    vehicle: request?.vehicle || {},
                    shop_ready_summary: request?.shop_ready_summary || {},
                    services: request?.services || [],
                    latest_estimate: request?.latest_estimate || null,
                    calendar: request?.calendar || {},
                }
                : null;
            const rescheduleRequest = ev.event === 'reschedule_requested'
                ? {
                    reschedule_request_id: ev.reschedule_request_id || '',
                    request_id: requestId,
                    reason: ev.reason || '',
                    status: ev.status || '',
                    previous_scheduled_start_at: ev.previous_scheduled_start_at || '',
                    previous_scheduled_end_at: ev.previous_scheduled_end_at || '',
                    previous_scheduled_timezone: ev.previous_scheduled_timezone || '',
                    created_at: ev.created_at || '',
                }
                : null;
            return {
                message_id: `event-${ev.event}-${ev.estimate_id || ev.created_at || i}`,
                request_id: requestId,
                sender_role: 'system',
                sender_label: 'Update',
                message_text: ev.label || String(ev.event || '').replace(/_/g, ' '),
                created_at: ev.created_at || '',
                is_status_update: true,
                event: ev.event,
                estimate,
                appointment,
                reschedule_request: rescheduleRequest,
            };
        });
}

export function buildPartnerRequestConversationMessages(request = {}, options = {}) {
    const initialMessage = buildPartnerRequestInitialMessage(request, options);
    const messages = Array.isArray(request?.messages) ? request.messages : [];
    const timelineItems = buildTimelineConversationItems(request);
    const merged = [
        ...(initialMessage ? [initialMessage] : []),
        ...messages,
        ...timelineItems,
    ];

    // Single chronological stream. Sort strictly by parsed timestamp; items with a missing/invalid
    // timestamp keep stable insertion order AFTER timestamped items (never jump to the top, which
    // is what previously threw estimates/status updates out of sequence).
    return merged
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
            const aTime = Date.parse(a.item?.created_at);
            const bTime = Date.parse(b.item?.created_at);
            const aValid = Number.isFinite(aTime);
            const bValid = Number.isFinite(bTime);
            if (aValid && bValid && aTime !== bTime) return aTime - bTime;
            if (aValid && !bValid) return -1;
            if (!aValid && bValid) return 1;
            // Equal or both-missing timestamps: initial request first, then stable insertion order.
            if (a.item?.is_initial_request && !b.item?.is_initial_request) return -1;
            if (!a.item?.is_initial_request && b.item?.is_initial_request) return 1;
            return a.index - b.index;
        })
        .map(({ item }) => item);
}
